import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";

const types = [
  { id: "customers", label: "Deleted Customers", icon: "groups" },
  { id: "complaints", label: "Deleted Complaints", icon: "report_problem" },
  { id: "reports", label: "Deleted Service Reports", icon: "assignment" },
  { id: "quotations", label: "Deleted Quotations", icon: "request_quote" },
  { id: "salesInvoices", label: "Deleted Sales Invoices", icon: "receipt_long" }
];

const formatCurrency = (value) => Number(value || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const getEndpointType = (type) => {
  if (type === "salesInvoices") return "sales-invoices";
  return type;
};

export default function RecycleBin() {
  const [activeType, setActiveType] = useState("customers");
  const [loading, setLoading] = useState(true);
  const [deletedCustomers, setDeletedCustomers] = useState([]);
  const [deletedComplaints, setDeletedComplaints] = useState([]);
  const [deletedReports, setDeletedReports] = useState([]);
  const [deletedQuotations, setDeletedQuotations] = useState([]);
  const [deletedSalesInvoices, setDeletedSalesInvoices] = useState([]);
  const [restoringId, setRestoringId] = useState("");
  const [deletingId, setDeletingId] = useState("");

  useEffect(() => {
    fetchDeletedItems();
  }, []);

  const fetchDeletedItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE_URL + "/api/admin/profile", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setDeletedCustomers(res.data.deletedCustomers || []);
      setDeletedComplaints(res.data.deletedComplaints || []);
      setDeletedReports(res.data.deletedReports || []);
      setDeletedQuotations(res.data.deletedQuotations || []);
      setDeletedSalesInvoices(res.data.deletedSalesInvoices || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load recycle bin records");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (type, id) => {
    try {
      setRestoringId(id);
      const endpoint = getEndpointType(type);
      await axios.put(API_BASE_URL + `/api/admin/${endpoint}/${id}/restore`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      alert("Item restored successfully!");
      fetchDeletedItems();
    } catch (err) {
      console.error(err);
      alert("Failed to restore item.");
    } finally {
      setRestoringId("");
    }
  };

  const handlePermanentDelete = async (type, id) => {
    if (!window.confirm("Are you sure you want to permanently delete this item? This action is irreversible.")) return;
    try {
      setDeletingId(id);
      const endpoint = getEndpointType(type);
      await axios.delete(API_BASE_URL + `/api/admin/${endpoint}/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      alert("Item permanently deleted!");
      fetchDeletedItems();
    } catch (err) {
      console.error(err);
      alert("Failed to permanently delete item.");
    } finally {
      setDeletingId("");
    }
  };

  const getList = () => {
    if (activeType === "customers") return deletedCustomers;
    if (activeType === "complaints") return deletedComplaints;
    if (activeType === "reports") return deletedReports;
    if (activeType === "quotations") return deletedQuotations;
    if (activeType === "salesInvoices") return deletedSalesInvoices;
    return [];
  };

  const currentList = getList();

  const renderItemDetails = (item) => {
    if (activeType === "customers") {
      return (
        <>
          <h3 className="font-bold text-slate-900 dark:text-white text-base truncate">
            {item.name || "Unnamed Customer"}
          </h3>
          <p className="text-xs text-slate-500 mt-1 line-clamp-1">
            Customer ID: <span className="font-medium text-slate-700 dark:text-slate-300">{item.customerId || "-"}</span>
          </p>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
            Contact: {item.phone || item.email || "-"}
          </p>
        </>
      );
    }

    if (activeType === "complaints") {
      return (
        <>
          <h3 className="font-bold text-slate-900 dark:text-white text-base truncate">
            {item.complaintId || "Complaint"}
          </h3>
          <p className="text-xs text-slate-500 mt-1 line-clamp-1">
            Company: <span className="font-medium text-slate-700 dark:text-slate-300">{item.company || item.customerName || "-"}</span>
          </p>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
            Nature: {item.natureOfComplaint || item.typeOfService || "-"}
          </p>
        </>
      );
    }

    if (activeType === "reports") {
      return (
        <>
          <h3 className="font-bold text-slate-900 dark:text-white text-base truncate">
            {item.reportId || item.visitId || "Service Report"}
          </h3>
          <p className="text-xs text-slate-500 mt-1 line-clamp-1">
            Customer: <span className="font-medium text-slate-700 dark:text-slate-300">{item.company || item.customerName || "-"}</span>
          </p>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
            Engineer: {item.engineerName || "-"}
          </p>
        </>
      );
    }

    if (activeType === "quotations") {
      return (
        <>
          <h3 className="font-bold text-slate-900 dark:text-white text-base truncate">
            {item.quotationNo || "Quotation"}
          </h3>
          <p className="text-xs text-slate-500 mt-1 line-clamp-1">
            Customer: <span className="font-medium text-slate-700 dark:text-slate-300">{item.customerName || "-"}</span>
          </p>
          <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mt-0.5 line-clamp-1">
            Total: ₹{formatCurrency(item.grandTotal || item.subTotal)}
          </p>
        </>
      );
    }

    if (activeType === "salesInvoices") {
      return (
        <>
          <h3 className="font-bold text-slate-900 dark:text-white text-base truncate">
            {item.documentNumber || item.poNumber || "Sales Invoice"}
          </h3>
          <p className="text-xs text-slate-500 mt-1 line-clamp-1">
            Customer: <span className="font-medium text-slate-700 dark:text-slate-300">{item.counterpartyName || "-"}</span>
          </p>
          <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mt-0.5 line-clamp-1">
            Amount: ₹{formatCurrency(item.finalTotal || item.totalAmount)}
          </p>
        </>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-4">
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Recycle Bin</h1>
        <p className="text-sm text-slate-500 mt-1">Review soft-deleted records. You can restore them to active modules or permanently purge them.</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-3">
        {types.map(t => {
          const active = activeType === t.id;
          let count = 0;
          if (t.id === "customers") count = deletedCustomers.length;
          if (t.id === "complaints") count = deletedComplaints.length;
          if (t.id === "reports") count = deletedReports.length;
          if (t.id === "quotations") count = deletedQuotations.length;
          if (t.id === "salesInvoices") count = deletedSalesInvoices.length;

          return (
            <button
              key={t.id}
              onClick={() => setActiveType(t.id)}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                active
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50"
              }`}
            >
              <span className="material-symbols-outlined text-sm">{t.icon}</span>
              <span>{t.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-blue-500 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-500"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading deleted records...</div>
        ) : currentList.length === 0 ? (
          <div className="text-center py-12 text-slate-500 italic">No deleted items found in this category.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentList.map((item) => (
              <div key={item._id} className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-slate-50/50 dark:bg-slate-900/30 flex flex-col justify-between min-h-[190px]">
                <div>
                  {renderItemDetails(item)}
                  <p className="text-[10px] text-slate-400 mt-2">
                    Deleted At: {item.deletedAt ? new Date(item.deletedAt).toLocaleDateString("en-GB") : (item.updatedAt ? new Date(item.updatedAt).toLocaleDateString("en-GB") : "-")}
                  </p>
                </div>
                <div className="flex gap-2 border-t pt-3 mt-3">
                  <button
                    onClick={() => handleRestore(activeType, item._id)}
                    disabled={restoringId === item._id || deletingId === item._id}
                    className="flex-1 flex items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2 text-xs font-semibold shadow-sm transition disabled:bg-slate-400"
                  >
                    <span className="material-symbols-outlined text-xs">restore</span>
                    {restoringId === item._id ? "Restoring..." : "Restore"}
                  </button>
                  <button
                    onClick={() => handlePermanentDelete(activeType, item._id)}
                    disabled={restoringId === item._id || deletingId === item._id}
                    className="flex-1 flex items-center justify-center gap-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2 text-xs font-semibold shadow-sm transition disabled:bg-slate-400"
                  >
                    <span className="material-symbols-outlined text-xs">delete</span>
                    {deletingId === item._id ? "Purging..." : "Delete"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
